import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  OnInit,
  computed,
  input,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DebounceTime } from '@olymp/shared';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import {
  MultiSelectModule,
  MultiSelectSelectAllChangeEvent,
} from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectService } from '../../utils/select.service';

const EMPTY_MULTI_OPTIONS: unknown[] = [];

@Component({
  selector: 'lib-multi-select-with-lazy',
  imports: [
    TranslocoModule,
    FormsModule,
    InputTextModule,
    MultiSelectModule,
    InputGroupModule,
    InputGroupAddonModule,
    CheckboxModule,
    DebounceTime,
  ],
  template: `
    <div class="w-full">
      @if (label()) {
        <label [for]="label()"
          >{{ label() | transloco }} <span class="text-red-600">*</span></label
        >
      }
      <p-multiselect
        appendTo="body"
        [virtualScroll]="virtualScroll() && !!api_url()"
        [virtualScrollItemSize]="48"
        [filter]="true"
        [options]="filteredOptions()"
        [optionLabel]="optionLabel()"
        [optionValue]="optionValue()"
        [ngModel]="value()"
        (ngModelChange)="onModelChange($event)"
        styleClass="w-full"
        panelStyleClass="lib-multiselect-panel"
        [virtualScrollOptions]="options"
        [filterBy]="filterFields() || 'name.uz'"
        [disabled]="isDisabled()"
        [showHeader]="false"
      >
        <ng-template #header>
          <div class="lib-multiselect-header">
            <div class="lib-multiselect-select-all">
              <p-checkbox
                [ngModel]="isAllSelected()"
                [binary]="true"
                [disabled]="isDisabled() || !getOptionsList().length"
                (onChange)="onSelectAllCheckboxChange($event.checked)"
                [inputId]="'select-all-' + (label() || 'multiselect')"
              />
              <label
                class="lib-multiselect-select-all-label"
                [for]="'select-all-' + (label() || 'multiselect')"
              >
                {{ 'select_all' | transloco }}
              </label>
            </div>
            <p-inputgroup class="lib-multiselect-search">
              <p-inputgroup-addon>
                <i class="pi pi-search"></i>
              </p-inputgroup-addon>
              <input
                type="text"
                pInputText
                libDebounceTime
                [ngModel]="filterValue()"
                (ngModelChange)="filterValue.set($event)"
                (searchValue)="getValueWithFilter()"
              />
            </p-inputgroup>
          </div>
        </ng-template>
        @if (isMultiLang()) {
          <ng-template let-value pTemplate="selectedItems">
            <span>{{ value?.length }} {{ 'items_selected' | transloco }}</span>
          </ng-template>
          <ng-template let-item #item>
            <span class="lib-multiselect-option-label">
              {{ multilangLabel(item?.name) }}
              @if (showInn() && item.inn) {
                <span class="lib-multiselect-option-inn"> — {{ item.inn }}</span>
              }
            </span>
          </ng-template>
        }
      </p-multiselect>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DatePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectWithLazy),
      multi: true,
    },
  ],
})
export class MultiSelectWithLazy<T>
  extends SelectService<T>
  implements ControlValueAccessor, OnInit
{
  private readonly transloco = inject(TranslocoService);

  showInn = input(false);

  multilangLabel(name: Record<string, string> | string | null | undefined): string {
    if (!name) {
      return '';
    }
    if (typeof name === 'string') {
      return name;
    }
    const lang = this.transloco.getActiveLang();
    return name[lang] || name['uz'] || name['ru'] || name['en'] || name['kaa'] || '';
  }

  isAllSelected = computed(() => {
    const currentFiltered = this.getFilteredList();
    if (!currentFiltered.length) {
      return false;
    }
    const valKey = this.optionValue() || 'value';
    return currentFiltered.every((item) =>
      this.isValueSelected((item as Record<string, unknown>)?.[valKey]),
    );
  });

  filteredOptions = computed(() => {
    const allOptions = this.getOptionsList();
    if (!allOptions.length) {
      return allOptions;
    }

    const valKey = this.optionValue() || 'value';
    const search = this.filterValue().toLowerCase().trim();

    const selectedItems = allOptions.filter((item) =>
      this.isValueSelected((item as Record<string, unknown>)?.[valKey]),
    );

    const matches = (
      search
        ? allOptions.filter((item) => this.matchesSearch(item, search))
        : allOptions
    ).slice(0, 100);

    const combined = [...selectedItems, ...matches];
    const seen = new Set<string>();
    return combined.filter((item) => {
      const id = this.normalizeOptionValue(
        (item as Record<string, unknown>)?.[valKey],
      );
      if (!id || seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
  });

  onModelChange(nextValue: T): void {
    const normalizedValue = this.normalizeModelValue(nextValue);
    const current = this.value();
    if (Array.isArray(normalizedValue) && Array.isArray(current)) {
      const nextArr = normalizedValue as unknown[];
      const curArr = current as unknown[];
      if (
        nextArr.length === curArr.length &&
        nextArr.every((item, index) => Object.is(item, curArr[index]))
      ) {
        return;
      }
    } else if (Object.is(current, normalizedValue)) {
      return;
    }

    this.value.set(normalizedValue);
    (this.onChange as (value?: T) => void)(normalizedValue);
    this.onTouched();
  }

  private normalizeModelValue(nextValue: T): T {
    if (!Array.isArray(nextValue)) {
      return nextValue;
    }

    const cleaned = [
      ...new Set(
        nextValue
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0),
      ),
    ];

    return cleaned as T;
  }

  getOptionsList(): T[] {
    return (this.inputOptions() ?? this.data() ?? EMPTY_MULTI_OPTIONS) as T[];
  }

  onSelectAllCheckboxChange(checked: boolean): void {
    this.onSelectAllToggle({
      originalEvent: new Event('change'),
      checked,
    });
  }

  private matchesSearch(item: T, search: string): boolean {
    const record = item as Record<string, unknown>;
    if (this.showInn()) {
      const inn = String(record['inn'] || '').toLowerCase();
      if (inn.includes(search)) {
        return true;
      }
    }

    if (this.isMultiLang()) {
      const name = record['name'] as Record<string, string> | undefined;
      return ['uz', 'ru', 'kaa', 'en'].some((lang) =>
        (name?.[lang] || '').toLowerCase().includes(search),
      );
    }

    const labelKey = this.optionLabel() || 'label';
    return String(record[labelKey] || '')
      .toLowerCase()
      .includes(search);
  }

  private normalizeOptionValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  private getSelectedValues(): unknown[] {
    const selected = this.value();
    if (selected === null || selected === undefined) {
      return [];
    }
    return Array.isArray(selected) ? selected : [selected];
  }

  isValueSelected(optionValue: unknown): boolean {
    const normalized = this.normalizeOptionValue(optionValue);
    if (!normalized) {
      return false;
    }
    return this.getSelectedValues().some(
      (selected) => this.normalizeOptionValue(selected) === normalized,
    );
  }

  getFilteredList(): T[] {
    const allOptions = this.getOptionsList();
    if (!allOptions.length) {
      return [];
    }

    const search = this.filterValue().toLowerCase().trim();
    if (!search) {
      return allOptions;
    }

    return allOptions.filter((item) => this.matchesSearch(item, search));
  }

  onSelectAllToggle(event: MultiSelectSelectAllChangeEvent): void {
    const valKey = this.optionValue() || 'value';
    const currentFiltered = this.getFilteredList();
    if (!currentFiltered.length) {
      return;
    }

    const selectedList = [...this.getSelectedValues()];
    const filteredIds = currentFiltered
      .map((item) => (item as Record<string, unknown>)[valKey])
      .filter((id) => Number.isInteger(Number(id)) && Number(id) > 0);
    const filteredIdSet = new Set(
      filteredIds.map((id) => this.normalizeOptionValue(id)),
    );

    let nextValue: unknown[];
    if (event.checked) {
      nextValue = [...selectedList];
      filteredIds.forEach((id) => {
        if (!this.isValueSelected(id)) {
          nextValue.push(id);
        }
      });
    } else {
      nextValue = selectedList.filter(
        (id) => !filteredIdSet.has(this.normalizeOptionValue(id)),
      );
    }

    this.onModelChange(nextValue as T);
  }
}
